// Two-step OTP sign-in: email -> 6-digit code. Also the value proposition for
// the progressive model — this is what unlocks the dashboard + push.
import { useState } from "react";
import { useRequestCode, useVerifyCode } from "../lib/auth";
import { ApiError } from "../lib/api";
import { Button, Card } from "./ui";

export function SignIn({ prefillEmail }: { prefillEmail?: string }) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(prefillEmail || "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const requestCode = useRequestCode();
  const verifyCode = useVerifyCode();

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await requestCode.mutateAsync(email.trim());
      setStep("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send a code.");
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await verifyCode.mutateAsync({ email: email.trim(), code: code.trim() });
      // On success the ['me'] cache flips to authenticated and the app re-renders.
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid code.");
    }
  }

  return (
    <div className="min-h-dvh flex flex-col justify-center px-6 pb-16 bg-canvas">
      <div className="text-center mb-8">
        <img src="/brand/tpfl.png" alt="Turbo Photo Film Lab" className="h-20 w-auto mx-auto" />
        <p className="text-ink-muted text-[15px] mt-4">
          Track your film, book pickups, pay, and download scans — all in one place.
        </p>
      </div>

      <Card className="p-5">
        {step === "email" ? (
          <form onSubmit={submitEmail} className="flex flex-col gap-3">
            <label className="text-sm font-medium text-ink">Your email</label>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-12 px-4 rounded-xl border border-line bg-canvas text-[16px] outline-none focus:border-navy"
            />
            <Button type="submit" disabled={requestCode.isPending}>
              {requestCode.isPending ? "Sending…" : "Email me a code"}
            </Button>
            <p className="text-[12px] text-ink-subtle text-center">
              We'll email a 6-digit code. No password needed.
            </p>
          </form>
        ) : (
          <form onSubmit={submitCode} className="flex flex-col gap-3">
            <label className="text-sm font-medium text-ink">
              Enter the code sent to <span className="text-navy">{email}</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="h-14 px-4 rounded-xl border border-line bg-canvas text-[28px] tracking-[10px] text-center font-mono outline-none focus:border-navy"
            />
            <Button type="submit" disabled={verifyCode.isPending || code.length < 6}>
              {verifyCode.isPending ? "Verifying…" : "Sign in"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
              className="text-[13px] text-ink-subtle underline"
            >
              Use a different email
            </button>
          </form>
        )}
        {error && <p className="text-sev-error text-sm mt-3">{error}</p>}
      </Card>
    </div>
  );
}
